import * as Sentry from "@sentry/solid";
import { ErrorBoundary, type ParentComponent, createEffect } from "solid-js";

/**
 * Per-route error boundary: a page whose render throws (typically a
 * `createResource` read while the BE is restarting) degrades to a scoped
 * fallback instead of taking down the whole shell — header/nav stay alive.
 *
 * "Try again" resets the boundary, which remounts the page component, so
 * its resources are re-created and refetch.
 *
 * Caught errors are still reported to Sentry: a boundary swallows what the
 * global onerror/onunhandledrejection handlers used to see (Z01-WEB-1), so
 * without the explicit capture we'd trade a bad signal for no signal.
 */
const PageBoundary: ParentComponent = (props) => (
  <ErrorBoundary
    fallback={(err, reset) => {
      // Effect, not render-time side effect: keeps the capture decoupled
      // from rendering (and safe if fallback evaluation semantics change).
      createEffect(() => Sentry.captureException(err));
      return (
        <div class="neuro-page">
          <div class="neuro-surface">
            <div class="neuro-route-body">
              <h2>something went wrong</h2>
              <p class="muted">{errorMessage(err)}</p>
              <button type="button" class="neuro-action" onClick={reset}>
                try again
              </button>
            </div>
          </div>
        </div>
      );
    }}
  >
    {props.children}
  </ErrorBoundary>
);

/**
 * Best human-readable message from an arbitrary thrown value.
 *
 * Solid's castError wraps non-Error throws as `Error("Unknown error",
 * {cause})` before the boundary sees them, so on that exact shape we dig
 * into `cause` for the real message (e.g. a bare ApiError body from the
 * legacy manual-DELETE helpers).
 */
function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.message === "Unknown error" && err.cause !== undefined) {
      return errorMessage(err.cause);
    }
    return err.message;
  }
  if (
    typeof err === "object" &&
    err !== null &&
    "message" in err &&
    typeof (err as { message: unknown }).message === "string"
  ) {
    return (err as { message: string }).message;
  }
  return String(err);
}

export default PageBoundary;
